import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '@/hooks/useAuth';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';

const loginSchema = z.object({
  email: z.string().email('Email invalide').min(1, 'Email requis'),
  password: z.string().min(1, 'Mot de passe requis'),
  remember: z.boolean().optional(),
});

type LoginFormData = z.infer<typeof loginSchema>;

export function LoginPage() {
  const { login, isLoading, error } = useAuth();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuthStore();
  const [showError, setShowError] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      remember: false,
    },
  });

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated() && user) {
      const userRoles = user.roles || [];
      if (userRoles.includes('ROLE_ROOT') || userRoles.includes('ROLE_SUPER_ROOT')) {
        navigate('/admin/dashboard', { replace: true });
      } else if (userRoles.includes('ROLE_ADMIN')) {
        navigate('/admin/dashboard', { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    }
  }, [isAuthenticated, user, navigate]);

  // Function to translate error messages to French
  const getErrorMessage = (error: unknown): string => {
    if (!error) return 'Une erreur est survenue';
    
    // Check if it's an axios error with response data
    if (typeof error === 'object' && 'response' in error) {
      const axiosError = error as { 
        response?: { 
          status?: number; 
          data?: { 
            message?: string;
            detail?: string;
            code?: number;
          } 
        }; 
        message?: string 
      };
      
      // Handle JSON response with message or detail field
      if (axiosError.response?.data) {
        const data = axiosError.response.data;
        // Check for message field (from our custom exception listener)
        if (data.message) {
          return data.message;
        }
        // Check for detail field (from Symfony error response)
        if (data.detail) {
          return data.detail;
        }
      }
      
      // Handle 401 Unauthorized
      if (axiosError.response?.status === 401) {
        return 'Email ou mot de passe incorrect';
      }
      
      // Handle axios error message
      if (axiosError.message) {
        if (axiosError.message.includes('401')) {
          return 'Email ou mot de passe incorrect';
        }
        if (axiosError.message.includes('Invalid credentials')) {
          return 'Email ou mot de passe incorrect';
        }
        if (axiosError.message.includes('Request failed')) {
          return 'Erreur de connexion. Veuillez réessayer.';
        }
      }
    }
    
    // Handle Error instances
    if (error instanceof Error) {
      const errorMessage = error.message;
      // Check for inactive user message
      if (errorMessage.includes('désactivé')) {
        return errorMessage; // Return the message as-is (already in French)
      }
      if (errorMessage.includes('401')) {
        return 'Email ou mot de passe incorrect';
      }
      if (errorMessage.includes('Invalid credentials')) {
        return 'Email ou mot de passe incorrect';
      }
      if (errorMessage.includes('Request failed')) {
        return 'Erreur de connexion. Veuillez réessayer.';
      }
      return errorMessage;
    }
    
    return 'Email ou mot de passe incorrect';
  };

  const onSubmit = (data: LoginFormData) => {
    setShowError(false);
    login(data);
  };

  const onError = () => {
    setShowError(false);
  };

  useEffect(() => {
    if (error) {
      setShowError(true);
    }
  }, [error]);

  const features = [
    {
      icon: 'domain',
      title: 'Gestion Multi-Projets',
      description: 'Centralisez la gestion de plusieurs bâtiments depuis une seule plateforme',
    },
    {
      icon: 'sensors',
      title: 'Surveillance IoT',
      description: 'Suivez en temps réel tous vos équipements et capteurs connectés',
    },
    {
      icon: 'warning',
      title: 'Alertes Intelligentes',
      description: 'Recevez des notifications instantanées en cas d\'anomalie détectée',
    },
    {
      icon: 'analytics',
      title: 'Tableaux de Bord',
      description: 'Visualisez vos données avec des graphiques et statistiques en temps réel',
    },
  ];

  return (
    <div className="font-display bg-background-light dark:bg-background-dark min-h-screen flex flex-row-reverse">
      {/* Right Side - Features Showcase */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-gradient-to-br from-primary/10 via-primary/5 to-transparent dark:from-primary/5 dark:via-transparent dark:to-transparent">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5 dark:opacity-10">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-primary rounded-full blur-[150px] -mr-64 -mt-64"></div>
          <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-primary rounded-full blur-[120px] -ml-48 -mb-48"></div>
        </div>

        <div className="relative z-10 flex flex-col justify-center px-12 py-16 w-full">
          {/* Logo & Branding */}
          <div className="mb-12">
            <div className="w-16 h-16 bg-primary/20 rounded-2xl flex items-center justify-center mb-6 border border-primary/30 backdrop-blur-sm">
              <span className="material-icons-outlined text-primary" style={{ fontSize: '32px' }}>domain</span>
            </div>
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-3">
              GTB Platform
            </h1>
            <p className="text-lg text-gray-600 dark:text-[#94A3B8]">
              La solution complète pour la gestion technique de vos bâtiments
            </p>
          </div>

          {/* Features Grid */}
          <div className="grid grid-cols-1 gap-6 mb-12">
            {features.map((feature, index) => (
              <div
                key={index}
                className="flex items-start gap-4 p-4 rounded-xl bg-white/50 dark:bg-[#1E293B]/50 backdrop-blur-sm border border-gray-200/50 dark:border-[#1E293B] hover:bg-white/70 dark:hover:bg-[#1E293B]/70 transition-all group"
              >
                <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-primary/10 dark:bg-primary/20 flex items-center justify-center group-hover:bg-primary/20 dark:group-hover:bg-primary/30 transition-colors">
                  <span className="material-icons-outlined text-primary" style={{ fontSize: '24px' }}>{feature.icon}</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-1">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-[#94A3B8]">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 pt-8 border-t border-gray-200 dark:border-[#1E293B]">
            <div className="text-center">
              <div className="text-2xl font-bold text-primary mb-1">100%</div>
              <div className="text-xs text-gray-600 dark:text-[#94A3B8]">Temps réel</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-primary mb-1">24/7</div>
              <div className="text-xs text-gray-600 dark:text-[#94A3B8]">Surveillance</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-primary mb-1">∞</div>
              <div className="text-xs text-gray-600 dark:text-[#94A3B8]">Évolutif</div>
            </div>
          </div>
        </div>
      </div>

      {/* Left Side - Login Form */}
      <div className="flex-1 flex flex-col justify-center px-6 py-12 lg:px-12 lg:py-16">
        <div className="w-full max-w-md mx-auto">
          {/* Mobile Logo */}
          <div className="lg:hidden flex flex-col items-center mb-10">
            <div className="w-16 h-16 bg-primary/20 rounded-2xl flex items-center justify-center mb-4 border border-primary/30">
              <span className="material-icons-outlined text-primary" style={{ fontSize: '32px' }}>domain</span>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              GTB Platform
            </h2>
            <p className="text-sm text-gray-600 dark:text-[#94A3B8] text-center">
              Accédez à votre interface de gestion technique
            </p>
          </div>

          {/* Desktop Header */}
          <div className="hidden lg:block mb-8">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              Connexion
            </h2>
            <p className="text-gray-600 dark:text-[#94A3B8]">
              Accédez à votre interface de gestion technique
            </p>
          </div>

          {/* Form Section */}
          <form className="space-y-5" onSubmit={handleSubmit(onSubmit, onError)} noValidate>
            {/* API Error Message */}
            {showError && error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-red-700 dark:text-red-400 text-sm">
                <div className="flex items-center gap-2">
                  <span className="material-icons-outlined" style={{ fontSize: '18px' }}>error</span>
                  <span>{getErrorMessage(error)}</span>
                </div>
              </div>
            )}

            {/* Validation Errors Summary */}
            {(errors.email || errors.password) && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-red-700 dark:text-red-400 text-sm">
                <p className="font-medium mb-2">Veuillez corriger les erreurs suivantes :</p>
                <ul className="list-disc list-inside space-y-1">
                  {errors.email && <li>{errors.email.message}</li>}
                  {errors.password && <li>{errors.password.message}</li>}
                </ul>
              </div>
            )}

            {/* Email Field */}
            <div className="flex flex-col gap-2">
              <label className="text-gray-700 dark:text-white text-sm font-medium">
                Identifiant
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <span className="material-icons-outlined text-gray-400 dark:text-[#94A3B8]" style={{ fontSize: '20px' }}>person</span>
                </div>
                <input
                  {...register('email', {
                    required: 'L\'identifiant est requis',
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: 'Email invalide'
                    }
                  })}
                  className={`w-full pl-12 pr-4 py-3 rounded-lg border text-gray-900 dark:text-white bg-white dark:bg-[#1E293B] focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all ${
                    errors.email 
                      ? 'border-red-500 dark:border-red-500' 
                      : 'border-gray-300 dark:border-[#1E293B]'
                  }`}
                  placeholder="nom@exemple.com"
                  type="text"
                  autoComplete="email"
                  disabled={isLoading}
                />
              </div>
              {errors.email && (
                <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>
              )}
            </div>

            {/* Password Field */}
            <div className="flex flex-col gap-2">
              <div className="flex justify-between items-center">
                <label className="text-gray-700 dark:text-white text-sm font-medium">
                  Mot de passe
                </label>
                <a
                  className="text-primary text-xs font-medium hover:underline"
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                  }}
                >
                  Mot de passe oublié ?
                </a>
              </div>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <span className="material-icons-outlined text-gray-400 dark:text-[#94A3B8]" style={{ fontSize: '20px' }}>lock</span>
                </div>
                <input
                  {...register('password', {
                    required: 'Le mot de passe est requis',
                    minLength: {
                      value: 1,
                      message: 'Le mot de passe est requis'
                    }
                  })}
                  className={`w-full pl-12 pr-4 py-3 rounded-lg border text-gray-900 dark:text-white bg-white dark:bg-[#1E293B] focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all ${
                    errors.password 
                      ? 'border-red-500 dark:border-red-500' 
                      : 'border-gray-300 dark:border-[#1E293B]'
                  }`}
                  placeholder="••••••••"
                  type="password"
                  autoComplete="current-password"
                  disabled={isLoading}
                />
              </div>
              {errors.password && (
                <p className="text-red-500 text-xs mt-1">{errors.password.message}</p>
              )}
            </div>

            {/* Remember me */}
            <div className="flex items-center gap-2">
              <input
                {...register('remember')}
                className="w-4 h-4 rounded border-gray-300 dark:border-[#1E293B] text-primary focus:ring-primary bg-white dark:bg-[#1E293B]"
                id="remember"
                type="checkbox"
                disabled={isLoading}
              />
              <label className="text-sm text-gray-600 dark:text-[#94A3B8]" htmlFor="remember">
                Rester connecté
              </label>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 px-4 bg-primary text-white rounded-lg font-semibold text-base shadow-lg shadow-primary/20 hover:bg-primary/90 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Connexion...</span>
                </>
              ) : (
                <>
                  <span>Se connecter</span>
                  <span className="material-icons-outlined" style={{ fontSize: '18px' }}>arrow_forward</span>
                </>
              )}
            </button>
          </form>

          {/* Footer */}
          <div className="mt-8 pt-6 border-t border-gray-200 dark:border-[#1E293B]">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4 text-xs text-gray-500 dark:text-[#94A3B8]">
              <p>© 2024 Building Control Systems</p>
              <div className="flex items-center gap-4">
                <a
                  className="hover:text-primary transition-colors cursor-pointer"
                  onClick={(e) => {
                    e.preventDefault();
                    alert('Support technique');
                  }}
                >
                  Support technique
                </a>
                <span className="text-gray-300 dark:text-gray-700">•</span>
                <span>v2.4.0</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
